import { createFileRoute } from '@tanstack/react-router'
import { useRef, useState } from "react";
// Serviços
import { useQuery } from "@tanstack/react-query";
import { get } from "@/services/api";
import useVerify from "@/hooks/use-verify";
import Pagination from "@/components/general-components/Pagination";
// Template Page list-form
import HeaderLists from "@/components/general-components/HeaderLists";
import SideForm from "@/components/general-components/SideForm";
import Dialog from "@/components/general-components/Dialog";
import ItemSkeleton from "./-skeletons/ItemSkeleton";
import ItemList from "./-components/CursosOnlineItem";
import Form from "./-components/CursosOnlineForm";
import SearchForm from "./-components/CursosOnlineSearch";
import CourseLessonsManager from "./-components/CourseLessonsManager";
// Interfaces
import { IEntity } from "./-interfaces/entity.interface";
import { ApiError } from "@/general-interfaces/api.interface";

export const Route = createFileRoute('/_authenticated/treinamentos/_cursos-online/cursos-online')({
  component: List,
})

function List() {
  const { can } = useVerify();
  const [openSearch, setOpenSearch] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState<IEntity | null>(null);
  const [openLessonsDialog, setOpenLessonsDialog] = useState(false);
  const [selectedModel, setSelectedModel] = useState<IEntity | null>(null);
  const [searchParams, setSearchParams] = useState({
    limit: 10,
    page: 0,
    show: ['course', 'lessons'],
    'order-name': 'asc',
  });
  const initialFormRef = useRef(searchParams);

  // Define variáveis de entidade
  const entity = {
    name: "Curso Online",
    pluralName: "Cursos Online",
    model: "online-courses",
    ability: "classes",
  }

  interface Response {
    rows: IEntity[];
    total: number;
  }

  const { data, isLoading, isError, error } = useQuery<Response | undefined, ApiError>({
    queryKey: [`list${entity.pluralName}`, searchParams],
    queryFn: async () => {
      const params = Object.keys(searchParams).map((key) => ({
        key,
        value: searchParams[key as keyof typeof searchParams]
      }));
      return get(entity.model, '', params);
    },
  });

  const handleSearch = async (params: { [key: string]: string | number | boolean | string[] }) => {
    setSearchParams((prev) => ({ ...prev, ...params }));
  };

  const handleClear = () => {
    setSearchParams(initialFormRef.current);
  };
  
  const handleLimitChange = (name: string, value: string | number) => {
    setSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  const handleManageLessons = (model: IEntity) => {
    setSelectedModel(model);
    setOpenLessonsDialog(true);
  };

  const totalPages = data ? Math.ceil(data.total / searchParams.limit) : 0;
  const skeletons = Array(5).fill(null);

  if (!can(`view_${entity.ability}`)) return null;

  return (
    <>
      <HeaderLists
        entityName={entity.name}
        ability={entity.ability}
        limit={data?.total || 0}
        searchParams={searchParams}
        onlimitChange={handleLimitChange}
        openSearch={setOpenSearch}
        openForm={setOpenForm}
        setFormData={setFormData} 
        setFormType={() => {}}
        iconForm="plus"
      />

      {/* Busca avançada */}
      <SideForm
        openSheet={openSearch}
        setOpenSheet={setOpenSearch}
        title={`Buscar ${entity.pluralName}`}
        description={`Preencha os campos abaixo para filtrar ${entity.pluralName}.`}
        side="left"
        form={ <SearchForm onSearch={handleSearch} onClear={handleClear} openSheet={setOpenSearch} params={searchParams} /> }
      />

      {/* Formulário */}
      <SideForm
        openSheet={openForm}
        setOpenSheet={setOpenForm}
        title={formData ? `Editar ${entity.name}` : `Cadastrar ${entity.name}`}
        description={formData ? `Edite as informações do ${entity.name}` : `Preencha os dados para cadastrar um novo ${entity.name}`}
        side="right"
        form={ <Form formData={formData} setOpenForm={setOpenForm} entity={entity} /> }
      />

      {/* Listagem de items */}
      <div className="space-y-2 mt-4">
        {isLoading
          ? skeletons.map((_, i) => <ItemSkeleton key={i} index={i} />)
          : isError
          ? <div className="w-full flex justify-center items-center font-medium text-destructive py-4 rounded border border-destructive">
              <p>Erro: {error?.response?.data?.message}</p>
            </div>
          : data?.rows && data.rows.length > 0 
          ? data.rows.map((item: IEntity, i: number) => (
              <ItemList 
                key={item.id} 
                item={item} 
                entity={entity}
                index={i} 
                setFormData={setFormData} 
                setOpenForm={setOpenForm}
                onManageLessons={handleManageLessons}
              />
            ))
          : (
            <div className="w-full flex justify-center items-center font-medium text-primary py-4 rounded border border-primary">
              <p>Nenhum {entity.name} encontrado!</p>
            </div>
          )
        }
      </div>

      {/* Paginação */}
      <div className="mt-4">
        {totalPages >= 1 && (
          <Pagination
            totalItems={data?.total || 0}
            itemsPerPage={searchParams.limit}
            currentPage={searchParams.page}
            onPageChange={handlePageChange}
            maxVisiblePages={5}
          />
        )}
      </div>

      {/* Dialog de Gerenciamento de Lições */}
      <Dialog
        open={openLessonsDialog}
        onOpenChange={setOpenLessonsDialog}
        title={`Vincular Aulas - ${selectedModel?.name || ''}`}
        description="Arraste as aulas para vinculá-las ao modelo do curso online"
        className="max-w-6xl"
        showBttn={false}
      >
        {selectedModel && (
          <CourseLessonsManager
            modelId={selectedModel.id}
            courseId={selectedModel.courseId}
            onClose={() => setOpenLessonsDialog(false)}
          />
        )}
      </Dialog>
    </>
  );
};

export default List;